import { t } from '@lingui/core/macro'
import { Alert, type AlertProps } from 'antd'
import { twMerge } from 'tailwind-merge'
import { useFormContext } from '../createAppFormHook'

type ErrorObject = {
  message: string
  code?: string
  path?: string[]
  expected?: string
  [key: string]: unknown
}

type ErrorListProps = {
  errors: ErrorObject[]
}

function ErrorList({ errors }: ErrorListProps) {
  return (
    <ul className="list-disc list-inside space-y-1">
      {errors.map(error => (
        <li key={error.message}>
          {error.message}
          {error.path && error.path.length > 0 && <span className="text-gray-500 ml-2">({error.path.join('.')})</span>}
        </li>
      ))}
    </ul>
  )
}

/**
 * Flattens TanStack Form errors structure into an array of error objects.
 * Handles nested error objects where each field can have an array of error objects.
 */
function flattenFormErrors(errors: unknown): ErrorObject[] {
  if (!errors) return []

  // errors is an array containing objects with field names as keys
  if (Array.isArray(errors)) {
    return errors.flatMap(errorObj => flattenFormErrors(errorObj))
  }

  // errors is an object where each key is a field name
  if (typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>).flatMap(([_field, fieldErrors]) => {
      // Each field can have an array of error objects
      if (Array.isArray(fieldErrors)) {
        return fieldErrors
          .map(err => {
            if (typeof err === 'object' && err !== null && 'message' in err) {
              return err as ErrorObject
            }
            if (typeof err === 'string') {
              return { message: err }
            }
            return null
          })
          .filter((err): err is ErrorObject => err !== null)
      }

      // Single error object
      if (typeof fieldErrors === 'object' && fieldErrors !== null && 'message' in fieldErrors) {
        return [fieldErrors as ErrorObject]
      }

      // String error
      if (typeof fieldErrors === 'string') {
        return [{ message: fieldErrors }]
      }

      return []
    })
  }

  return []
}

export type FormValidationAlertProps = AlertProps & {
  /**
   * When true (default), the button only renders if the form has changes (isDirty).
   * Set to false to always render the button regardless of dirty state.
   */
  showOnlyWhenDirty?: boolean
}

/**
 * Ant Design based submit button that integrates with TanStack Form state.
 * - Shows loading while submitting
 * - Disables when the form cannot submit
 * - Shows a helpful tooltip when disabled (validation friendly)
 */
export function FormValidationAlert(props: FormValidationAlertProps) {
  const form = useFormContext()
  const { showOnlyWhenDirty = false, className, ...alertProps } = props

  return (
    <form.Subscribe
      selector={state => ({
        isSubmitting: state.isSubmitting,
        isDirty: state.isDirty,
        errors: state.errors,
      })}
    >
      {({ isSubmitting, isDirty, errors }) => {
        if ((!isDirty && showOnlyWhenDirty) || isSubmitting) {
          return null
        }

        const fieldErrors = flattenFormErrors(errors)
        const count = fieldErrors.length

        if (count === 0) {
          return null
        }

        return (
          <Alert
            type="warning"
            {...alertProps}
            className={twMerge(className)}
            title={t`${count} Validierungsfehler`}
            description={<ErrorList errors={fieldErrors} />}
          />
        )
      }}
    </form.Subscribe>
  )
}
